/**
 * Branded ID types for the Tragfähigkeits-App.
 *
 * Every persisted entity gets its own ID brand so that a `CaseId` cannot be
 * accidentally passed where a `UserId` is expected. The runtime guard is a
 * standard UUID v4-compatible regex (UUIDv1–v5 accepted, lower-case canonical
 * form). Higher entropy is enforced by the database, not by us.
 */
import { z } from 'zod';

// Nominal Brand for IDs — string-literal property name (siehe brands.ts:
// gleiches TS4023-Problem mit `unique symbol`).
export type IdBrand<B extends string> = string & { readonly __idBrand: B };

export type CaseId = IdBrand<'CaseId'>;
export type UserId = IdBrand<'UserId'>;
export type JobcenterId = IdBrand<'JobcenterId'>;
export type StatementId = IdBrand<'StatementId'>;
export type DimensionDataId = IdBrand<'DimensionDataId'>;
export type FinancialDatasetId = IdBrand<'FinancialDatasetId'>;
export type ScoreId = IdBrand<'ScoreId'>;
export type AuditLogEntryId = IdBrand<'AuditLogEntryId'>;

// RFC 4122 v1-v5 UUID, canonical lower-case form. We do NOT accept upper-case
// because Postgres `uuid` type returns lower-case; round-tripping must be
// loss-less.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const uuidIdSchema = <B extends string>(name: B) =>
  z
    .string()
    .regex(UUID_REGEX, `${name} must be a lower-case RFC 4122 UUID`)
    .transform((v) => v as IdBrand<B>);

export const CaseIdSchema = uuidIdSchema('CaseId');
export const UserIdSchema = uuidIdSchema('UserId');
export const JobcenterIdSchema = uuidIdSchema('JobcenterId');
export const StatementIdSchema = uuidIdSchema('StatementId');
export const DimensionDataIdSchema = uuidIdSchema('DimensionDataId');
export const FinancialDatasetIdSchema = uuidIdSchema('FinancialDatasetId');
export const ScoreIdSchema = uuidIdSchema('ScoreId');
export const AuditLogEntryIdSchema = uuidIdSchema('AuditLogEntryId');
